import { useState } from "react";
import { api, type Booking, type BookingLineItem, type Package } from "../../lib/api";
import { formatPrice } from "../../lib/format";
import { Modal } from "../ui/Modal";

type DraftItem = {
  id: string;      // existing ID or "" for new
  packageId: string;
  name: string;
  description: string;
  priceCents: number;
  currency: string;
  quantity: number;
};

export function LineItemsModal({ booking, packages, onClose, onSaved }: {
  booking: Booking;
  packages: Package[];
  onClose: () => void;
  onSaved: (items: BookingLineItem[]) => void;
}) {
  const [items, setItems] = useState<DraftItem[]>(() => {
    if (booking.LineItems && booking.LineItems.length > 0) {
      return booking.LineItems.map(li => ({
        id: li.ID,
        packageId: li.PackageID,
        name: li.Name,
        description: li.Description,
        priceCents: li.PriceCents,
        currency: li.Currency || "EUR",
        quantity: li.Quantity,
      }));
    }
    return [];
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function addCustomItem() {
    setItems(prev => [...prev, {
      id: "", packageId: "", name: "", description: "",
      priceCents: 0, currency: "EUR", quantity: 1,
    }]);
  }

  function loadFromPackage(index: number, pkgId: string) {
    const pkg = packages.find(p => p.ID === pkgId);
    setItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      return {
        ...item,
        packageId: pkgId,
        name: pkg ? pkg.Name : item.name,
        priceCents: pkg ? pkg.PriceCents : item.priceCents,
        currency: pkg ? pkg.Currency : item.currency,
      };
    }));
  }

  function updateItem(index: number, patch: Partial<DraftItem>) {
    setItems(prev => prev.map((item, i) => i === index ? { ...item, ...patch } : item));
  }

  function removeItem(index: number) {
    setItems(prev => prev.filter((_, i) => i !== index));
  }

  async function handleSave() {
    for (const item of items) {
      if (!item.name.trim()) {
        setError("All items must have a name");
        return;
      }
      if (item.priceCents < 0) {
        setError("Price must be 0 or positive");
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      const result = await api.setBookingLineItems(booking.ID, items.map(item => ({
        package_id: item.packageId || undefined,
        name: item.name,
        description: item.description,
        price_cents: item.priceCents,
        currency: item.currency || "EUR",
        quantity: item.quantity || 1,
      })));
      onSaved(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const totalCents = items.reduce((s, i) => s + i.priceCents * i.quantity, 0);
  const currency = items.find(i => i.currency)?.currency ?? booking.Currency ?? "EUR";

  return (
    <Modal open onClose={onClose} title={`Packages & services — ${booking.Title}`} maxWidth={700} zIndex={400}>
      {error && <div className="alert alert-error" style={{ marginBottom: "0.75rem" }}>{error}</div>}

        {items.length > 0 ? (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", marginBottom: "0.75rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border)" }}>
                <th style={{ textAlign: "left", padding: "0.25rem 0.5rem", fontWeight: 600 }}>Package / Item</th>
                <th style={{ textAlign: "left", padding: "0.25rem 0.5rem", fontWeight: 600 }}>Description</th>
                <th style={{ textAlign: "right", padding: "0.25rem 0.5rem", fontWeight: 600 }}>Qty</th>
                <th style={{ textAlign: "right", padding: "0.25rem 0.5rem", fontWeight: 600 }}>Price</th>
                <th style={{ padding: "0.25rem 0.5rem" }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "0.35rem 0.5rem", verticalAlign: "top" }}>
                    {packages.length > 0 && (
                      <select
                        value={item.packageId}
                        onChange={e => loadFromPackage(i, e.target.value)}
                        style={{ display: "block", width: "100%", marginBottom: "0.25rem", fontSize: "0.8rem" }}
                      >
                        <option value="">Custom item</option>
                        {packages.map(p => (
                          <option key={p.ID} value={p.ID}>{p.Name}</option>
                        ))}
                      </select>
                    )}
                    <input
                      required
                      placeholder="Name *"
                      value={item.name}
                      onChange={e => updateItem(i, { name: e.target.value })}
                      style={{ width: "100%", fontSize: "0.85rem" }}
                    />
                  </td>
                  <td style={{ padding: "0.35rem 0.5rem", verticalAlign: "top" }}>
                    <input
                      placeholder="Description"
                      value={item.description}
                      onChange={e => updateItem(i, { description: e.target.value })}
                      style={{ width: "100%", fontSize: "0.85rem" }}
                    />
                  </td>
                  <td style={{ padding: "0.35rem 0.5rem", verticalAlign: "top", width: 80 }}>
                    <input
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={e => updateItem(i, { quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                      style={{ width: "100%", minWidth: 64, fontSize: "0.85rem", textAlign: "right" }}
                    />
                  </td>
                  <td style={{ padding: "0.35rem 0.5rem", verticalAlign: "top", width: 100 }}>
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={item.priceCents / 100}
                      onChange={e => updateItem(i, { priceCents: Math.round(parseFloat(e.target.value.replace(",", ".")) * 100) || 0 })}
                      style={{ width: "100%", fontSize: "0.85rem", textAlign: "right" }}
                    />
                  </td>
                  <td style={{ padding: "0.35rem 0.5rem", verticalAlign: "top" }}>
                    <button
                      type="button"
                      className="btn-ghost"
                      style={{ padding: "0.2rem 0.4rem", fontSize: "0.75rem", color: "#ef4444" }}
                      onClick={() => removeItem(i)}
                    >
                      &#10007;
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} style={{ padding: "0.5rem", textAlign: "right", fontWeight: 600, fontSize: "0.85rem" }}>Total</td>
                <td style={{ padding: "0.5rem", textAlign: "right", fontWeight: 600, fontSize: "0.85rem" }}>
                  {formatPrice(totalCents, currency)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        ) : (
          <p style={{ color: "var(--muted)", fontSize: "0.875rem", margin: "0 0 0.75rem" }}>No items yet.</p>
        )}

        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
          <button type="button" className="btn-ghost" style={{ fontSize: "0.85rem" }} onClick={addCustomItem}>
            + Add custom item
          </button>
          {packages.map(p => (
            <button
              key={p.ID}
              type="button"
              className="btn-ghost"
              style={{ fontSize: "0.8rem" }}
              onClick={() => {
                setItems(prev => [...prev, {
                  id: "",
                  packageId: p.ID,
                  name: p.Name,
                  description: p.Description,
                  priceCents: p.PriceCents,
                  currency: p.Currency,
                  quantity: 1,
                }]);
              }}
            >
              + {p.Name}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: "0.5rem" }}>
        <button type="button" onClick={onClose} className="btn-ghost" style={{ flex: 1 }}>Cancel</button>
        <button type="button" onClick={handleSave} className="btn-primary" disabled={saving} style={{ flex: 2 }}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

